/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-this-alias */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { v4 as UUIDv4 } from 'uuid';
import { SessionServiceService } from '../session-service.service';
import { environment } from 'src/environments/environment';
import Utils from './utils';
import { UI_ACTION_CONSTANTS } from './constants';
import { LogItemModel } from './models/log-item.model';

@Injectable({
  providedIn: 'root'
})
export class LoggerService {

  private static readonly BATCH_SIZE: number = 2;
  private static readonly BATCH_TIMEOUT: number = 10000;
  private static readonly SEND_TIMEOUT: number = 5000;
  private static readonly MAX_RETRIES: number = 3;

  // Use the API URL from environment, but fall back to nothing if it's localhost for development
  private baseURL = (environment.API_URL && !environment.API_URL.includes('localhost')) 
    ? environment.API_URL + '/logs'
    : null; // Set to null if it's localhost to avoid connection errors
  // initialize the userConsent from local storage value if it exists
  private userConsent: boolean = localStorage["userConsent"] ? (localStorage["userConsent"] === "true") : false;
  // initialize the queue with local storage elements if it exists
  private batchQueue: Array<LogItemModel> = localStorage["logs"] ? JSON.parse(localStorage["logs"]) : [];
  private batchTimerID: any = null;
  // number of log items thata re currently being sent in the outstanding request
  private noOfLogItemsBeingSent: number = 0;
  private sendTimerID: any = null;
  private noOfRetries: number = 0;

  private sessionUUID: string;
  private toolVersion: string;
  private toolLanguage: string;
  private country: string;

  constructor(
    private _session: SessionServiceService,
    private _httpClient: HttpClient
  ) {
    this.toolVersion = this._session.getCurrentToolVersion();
    this.toolLanguage = this._session.getCurrentLocale();
    this.country = Utils.getCountry();
  }

  public setUserConsent(userConsent: boolean) {
    this.userConsent = userConsent;
    // also store the userConsent in local storage so that the user doesn't have to consent again
    localStorage.setItem("userConsent", userConsent.toString());
    // if the user rejects, we post the logs that were already in the local storage
    if (!userConsent) {
      this.sendBatchIfComplete();
    }
  }

  public getUserConsent() {
    return this.userConsent;
  }

  public log(uiAction: string, metadata?: any, result?: any) {
    // proceed only if you have user consent for logging
    // TODO: what to do about the logs that were initialized from local storage?
    if (this.userConsent) {
      let logItem = null;

      if (uiAction === UI_ACTION_CONSTANTS.OER_CONVERSION_PROCESS_STARTED) {
        // create new actor name based on the session UUID
        this.sessionUUID = this._session.getCurrentSessionUUID();

        logItem = {
          id: UUIDv4(),
          timestamp: (new Date()).toISOString(),
          sessionId: this.sessionUUID,
          action: {
            name: uiAction
          },
          context: {
            toolVersion: this.toolVersion,
            toolLanguage: this.toolLanguage,
            country: this.country,
          }
        };

      } else if (this.sessionUUID) {
        logItem = {
          id: UUIDv4(),
          timestamp: (new Date()).toISOString(),
          sessionId: this.sessionUUID,
          action: {
            name: uiAction,
            metadata: metadata,
            result: result
          }
        };
      }

      if (logItem !== null) {
        console.log(logItem);
        this.addLogItemToQueue(logItem);
      }
    }
  }

  private addLogItemToQueue(logItem: LogItemModel): void {
    this.batchQueue = this.batchQueue.concat(logItem);
    // also store in local storage for backup
    // this helps if something goes wrong such as browser crash etc
    // helps to send the logs once the tool is opened back again
    // since log statements have id's, there won't be any duplicates in Learning Locker
    console.time("localStorage.setItem");
    localStorage.setItem("logs", JSON.stringify(this.batchQueue));
    console.timeEnd("localStorage.setItem");

    console.time("localStorage");
    JSON.parse(localStorage["logs"]);
    console.timeEnd("localStorage");

    console.log("-------------------------------");
    console.log("--- Queue Length: " + this.batchQueue.length);
    console.log("--- Queue: ");
    console.log(this.batchQueue);
    console.log("-------------------------------");

    // set the timer if this is the first item in the queue
    // this ensures items are sent within the timeout period
    if (!this.batchTimerID) {
      const that = this;
      this.batchTimerID = setTimeout(function () {
        that.sendBatch.call(that);
      }, LoggerService.BATCH_TIMEOUT);
    }

    this.sendBatchIfComplete();
  }

  private batchQueueHasOverdueMessages(): boolean {
    for (let i: number = 0; i < this.batchQueue.length; i++) {
        const messageAge: number = (new Date).getTime() - new Date(this.batchQueue[i].timestamp).getTime();
        if (messageAge > LoggerService.BATCH_TIMEOUT) {
          return true;
        }
    }
    return false;
  }

  private sendBatchIfComplete(): void {
    if ((this.batchQueue.length >= LoggerService.BATCH_SIZE) || this.batchQueueHasOverdueMessages()) {
        this.sendBatch();
    }
  }

  private onSendingEnded(): void {
    if (this.sendTimerID) {
      clearTimeout(this.sendTimerID);
      this.sendTimerID = null;
    }
    this.noOfLogItemsBeingSent = 0;
    this.sendBatchIfComplete();
  }

  private sendBatch(): void {
    // If baseURL is null, skip sending logs (typically in development environment)
    if (!this.baseURL) {
      console.log('Logging endpoint not available, skipping log send');
      // Clear the queue since we won't be sending these logs
      this.batchQueue = [];
      localStorage.setItem("logs", JSON.stringify(this.batchQueue));
      
      // Reset timers
      if (this.batchTimerID) {
        clearTimeout(this.batchTimerID);
        this.batchTimerID = null;
      }
      
      return;
    }
    
    // if there are log items in the outstanding request, do not proceed to clear the batch timer
    // the messages that were stopped from going out may get ignored because the batch timer never went off
    if (this.noOfLogItemsBeingSent > 0) {
      return;
    }

    // check if we have reached MAX_RETRIES
    // if yes, do not proceed, reset the noOfRetries
    // else, resend the request MAX_RETRIES no of times
    if (this.noOfRetries >= LoggerService.MAX_RETRIES) {
      this.noOfRetries = 0;
      return;
    }

    if (this.batchTimerID) {
        clearTimeout(this.batchTimerID);
        this.batchTimerID = null;
    }

    if (this.batchQueue.length === 0) {
        return;
    }

    this.noOfLogItemsBeingSent = this.batchQueue.length;
    const that = this;

    // setTimer for sending
    if (!this.sendTimerID) {
      this.sendTimerID = setTimeout(function () {
        // increment the no of retries
        that.noOfRetries = that.noOfRetries + 1;
        that.onSendingEnded.call(that);
      }, LoggerService.SEND_TIMEOUT);
    }

    // make a POST request to learning locker asynchronously
    this._httpClient.post<any>(
      this.baseURL,
      this.batchQueue,
      {
        headers: {
          "Content-Type": "application/json; charset=utf-8"
        },
        observe: "response"
    }).toPromise().then((res) => {
      // log items have been successfully sent to server
      // remove the logs thats were sent in the batch queue
      // this can be done by removing the first noOfLogItemsBeingSent items
      that.batchQueue.splice(0, that.noOfLogItemsBeingSent);
      console.log("----------------------------------------");
      console.log("------- Request sucessfully sent -------");
      console.log("-----------> Queue Length: " + that.batchQueue.length);
      console.log("----------------------------------------");
      // reset the noOfRetries
      that.noOfRetries = 0;
      // clear the sent logs from the local storage by storing the updated batchQueue
      localStorage.setItem("logs", JSON.stringify(that.batchQueue));
      that.onSendingEnded.call(that);
    }).catch((error) => {
      console.log(error);
    });
  }

  // made this public, so that it can be called from app.component onDestroy, when the page is unloadedd
  public async handleOnDestroy(): Promise<void> {
    console.log("handleOnDestroy: Sending logs to server ....");

    // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon
    // The navigator.sendBeacon() method asynchronously sends an HTTP POST request containing a 
    // small amount of data to a web server.
    // It's intended to be used for sending analytics data to a web server, and avoids some of the problems 
    // with legacy techniques for sending analytics, such as the use of XMLHttpRequest.
    navigator.sendBeacon(this.baseURL, new Blob([JSON.stringify(this.batchQueue)], {
      type: "application/json; charset=utf-8",
    }));

    // suppose the navigator.sendBeacon() fails in sending the request (due to error from server or connection loss)
    // the logs have already been stored in the local storage for backup
    // this helps to send the logs  once the tool is opened back again
    // since log statements have id's, there won't be any duplicates in Learning Locker
  }
};
